import React, { useEffect, useState, useCallback } from "react";
import {
  getWeaponTemplates,
  getWeaponCategories,
} from "../../services/weaponTemplates";
import "./WeaponSelector.css";

function WeaponSelector({ character, onSelect, onClose }) {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("TUTTE");
  const [weapons, setWeapons] = useState([]);
  const [filteredWeapons, setFilteredWeapons] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  const filterWeapons = useCallback(() => {
    let filtered = [...weapons];

    // Filtra per categoria
    if (selectedCategory !== "TUTTE") {
      filtered = filtered.filter(
        (weapon) => weapon.category === selectedCategory,
      );
    }

    // Filtra per ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((weapon) =>
        weapon.name.toLowerCase().includes(term),
      );
    }

    setFilteredWeapons(filtered);
  }, [weapons, selectedCategory, searchTerm]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterWeapons();
  }, [filterWeapons]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, weaponsData] = await Promise.all([
        getWeaponCategories(),
        getWeaponTemplates(),
      ]);

      setCategories(["TUTTE", ...categoriesData.categories]);
      setWeapons(weaponsData.weaponTemplates);
      setFilteredWeapons(weaponsData.weaponTemplates);
    } catch (error) {
      console.error("Error loading weapons:", error);
      alert("Errore nel caricamento delle armi");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectWeapon = (weaponTemplate) => {
    // Cerca la skill del personaggio che corrisponde alla baseSkill del template
    const characterSkill = character.skills.find(
      (skill) => skill.name === weaponTemplate.baseSkill,
    );

    // Converti il template in formato arma per il personaggio
    const weapon = {
      name: weaponTemplate.name,
      // Usa il valore della skill del personaggio se esiste, altrimenti il baseSkillValue del template
      value: characterSkill
        ? characterSkill.value
        : weaponTemplate.baseSkillValue?.toString() || "0",
      associatedSkill: weaponTemplate.baseSkill || "",
      damage: weaponTemplate.damage || "",
      range: weaponTemplate.range || "",
      attacks: weaponTemplate.attacksPerRound || "",
      ammo: weaponTemplate.magazine || "",
      malfunction: weaponTemplate.malfunzionamento || "",
    };

    onSelect(weapon, weaponTemplate.baseSkillValue);
  };

  return (
    <div className="weapon-selector-overlay" onClick={onClose}>
      <div
        className="weapon-selector-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="weapon-selector-header">
          <h2>Seleziona Arma</h2>
          <button className="weapon-selector-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* Barra di ricerca */}
        <div className="weapon-selector-search">
          <input
            type="text"
            placeholder="Cerca arma..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Tabs categorie */}
        <div className="weapon-selector-tabs">
          {categories.map((category) => (
            <button
              key={category}
              className={`weapon-selector-tab ${
                selectedCategory === category ? "active" : ""
              }`}
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Lista armi */}
        <div className="weapon-selector-list">
          {loading ? (
            <div className="weapon-selector-loading">Caricamento...</div>
          ) : filteredWeapons.length === 0 ? (
            <div className="weapon-selector-empty">Nessuna arma trovata</div>
          ) : (
            filteredWeapons.map((weapon) => (
              <div
                key={weapon._id}
                className="weapon-selector-item"
                onClick={() => handleSelectWeapon(weapon)}
              >
                <div className="weapon-selector-item-header">
                  <span className="weapon-selector-item-name">
                    {weapon.name}
                  </span>
                  <span className="weapon-selector-item-cost">
                    {weapon.cost}
                  </span>
                </div>
                <div className="weapon-selector-item-details">
                  <span>Danno: {weapon.damage}</span>
                  <span>Gittata: {weapon.range}m</span>
                  <span>Colpi: {weapon.attacksPerRound}</span>
                  {weapon.magazine && <span>Munizioni: {weapon.magazine}</span>}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="weapon-selector-footer">
          <button className="weapon-selector-cancel" onClick={onClose}>
            Annulla
          </button>
        </div>
      </div>
    </div>
  );
}

export default WeaponSelector;
